import { TIER_DISPLAY_NAMES, TIER_MIN_STORAGE } from '@/atoms/subscription.atom';
import { userAtom } from '@/atoms/user.atom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { execute } from '@/graphql/execute';
import { BillingInterval, CollectionMethod, SubscriptionTier } from '@/graphql/graphql';
import { usePricing } from '@/hooks/usePricing';
import { CreateSubscriptionParams, useSubscription } from '@/hooks/useSubscription';
import { validatePromoCodeQuery } from '@/queries/subscription.query';
import { useAtomValue } from 'jotai';
import { Check, CreditCard, Loader2, Tag, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { StorageManager } from './StorageManager';

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TierOption {
  tier: SubscriptionTier;
  name: string;
  description: string;
  features: string[];
}

const TIER_OPTIONS: TierOption[] = [
  {
    tier: 'LITE' as SubscriptionTier,
    name: 'Lite',
    description: 'For individuals',
    features: ['1 user', 'Max 5 projects', 'Max 100 GB'],
  },
  {
    tier: 'CORE' as SubscriptionTier,
    name: 'Core',
    description: 'For teams',
    features: ['Up to 10 users', 'Unlimited projects', 'DWG support'],
  },
  {
    tier: 'PLUS' as SubscriptionTier,
    name: 'Plus',
    description: 'For professionals',
    features: ['Unlimited users', 'GIS toolkit', 'Branding'],
  },
  {
    tier: 'ENTERPRISE' as SubscriptionTier,
    name: 'Enterprise',
    description: 'For organizations',
    features: ['SSO', 'Mobile app', 'Custom domain'],
  },
];

export const CheckoutDialog = ({ open, onOpenChange }: CheckoutDialogProps) => {
  const { createSubscription, loading } = useSubscription();
  const { calculateStoragePrice, formatPrice } = usePricing();
  const user = useAtomValue(userAtom);
  const [step, setStep] = useState<'tier' | 'storage' | 'payment' | 'promo'>('tier');
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>('CORE' as SubscriptionTier);
  const [storageBlocks, setStorageBlocks] = useState(TIER_MIN_STORAGE['CORE' as SubscriptionTier]);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('ANNUAL' as BillingInterval);
  const [collectionMethod, setCollectionMethod] = useState<CollectionMethod>('AUTOMATIC' as CollectionMethod);
  const [promoCode, setPromoCode] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState<{
    valid: boolean;
    name: string | null;
    percentOff: number | null;
    amountOff: number | null;
  } | null>(null);

  const monthlyPrice = useMemo(
    () => calculateStoragePrice(selectedTier, storageBlocks, 'MONTHLY' as BillingInterval),
    [calculateStoragePrice, selectedTier, storageBlocks],
  );
  const annualPrice = useMemo(
    () => calculateStoragePrice(selectedTier, storageBlocks, 'ANNUAL' as BillingInterval),
    [calculateStoragePrice, selectedTier, storageBlocks],
  );
  const annualSavings = monthlyPrice * 12 - annualPrice;

  const handleNext = () => {
    if (step === 'tier') setStep('storage');
    else if (step === 'storage') setStep('payment');
    else if (step === 'payment') setStep('promo');
  };

  const handleBack = () => {
    if (step === 'storage') setStep('tier');
    else if (step === 'payment') setStep('storage');
    else if (step === 'promo') setStep('payment');
  };

  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleCheckout = async () => {
    setCheckoutError(null);
    try {
      const params: CreateSubscriptionParams = {
        tier: selectedTier,
        storageBlocks,
        billingInterval,
        collectionMethod,
        promoCode: promoCode || undefined,
        successUrl: `${window.location.origin}${window.location.pathname}?checkout=success`,
        cancelUrl: `${window.location.origin}${window.location.pathname}?checkout=canceled`,
        userEmail: user?.email || undefined,
      };

      const result = await createSubscription(params);

      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout. Please try again.';
      setCheckoutError(message);
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>
            {step === 'tier' && 'Choose Your Plan'}
            {step === 'storage' && 'Select Storage'}
            {step === 'payment' && 'Payment Method'}
            {step === 'promo' && 'Review & Checkout'}
          </DialogTitle>
          <DialogDescription>
            {step === 'tier' && 'Select the plan that best fits your needs.'}
            {step === 'storage' && 'Choose how much storage you need.'}
            {step === 'payment' && 'Select your preferred payment method.'}
            {step === 'promo' && 'Review your selection and apply any promo codes.'}
          </DialogDescription>
        </DialogHeader>

        <div className='py-4'>
          {/* Step 1: Tier Selection */}
          {step === 'tier' && (
            <div className='grid grid-cols-2 gap-3'>
              {TIER_OPTIONS.map((option) => (
                <div
                  key={option.tier}
                  className={`relative rounded-lg border p-3 cursor-pointer transition-all ${
                    selectedTier === option.tier
                      ? 'border-blue-500 ring-2 ring-blue-500'
                      : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                  }`}
                  onClick={() => {
                    setSelectedTier(option.tier);
                    // Update storage blocks to tier minimum if current is below
                    const tierMin = TIER_MIN_STORAGE[option.tier];
                    if (storageBlocks < tierMin) {
                      setStorageBlocks(tierMin);
                    }
                  }}>
                  <div className='flex items-start justify-between'>
                    <div>
                      <h3 className='font-semibold text-slate-900 dark:text-white text-sm'>{option.name}</h3>
                      <p className='text-xs text-slate-500 dark:text-stone-400'>{option.description}</p>
                    </div>
                    {selectedTier === option.tier && (
                      <div className='h-4 w-4 rounded-full bg-blue-500 flex items-center justify-center shrink-0'>
                        <Check className='h-2.5 w-2.5 text-white' />
                      </div>
                    )}
                  </div>
                  <ul className='mt-2 space-y-0.5'>
                    {option.features.map((feature, i) => (
                      <li key={i} className='flex items-center gap-1.5 text-xs text-slate-600 dark:text-stone-300'>
                        <Check className='h-3 w-3 text-green-500 shrink-0' />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Storage Selection */}
          {step === 'storage' && (
            <div className='space-y-6'>
              <StorageManager
                standalone
                overrideTier={selectedTier}
                overrideBlocks={storageBlocks}
                overrideBillingInterval={billingInterval}
                onStorageChange={setStorageBlocks}
              />

              <div>
                <Label>Billing Cycle</Label>
                <RadioGroup
                  value={billingInterval}
                  onValueChange={(v) => setBillingInterval(v as BillingInterval)}
                  className='mt-2 space-y-2'>
                  <label
                    htmlFor='annual'
                    className={`flex items-center justify-between rounded-lg border p-3 cursor-pointer transition-all ${
                      billingInterval === 'ANNUAL'
                        ? 'border-blue-500 ring-2 ring-blue-500'
                        : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                    }`}>
                    <div className='flex items-center gap-2'>
                      <RadioGroupItem value='ANNUAL' id='annual' />
                      <div>
                        <span className='font-medium'>Annual</span>
                        {annualSavings > 0 && (
                          <span className='ml-2 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded'>
                            Save {formatPrice(annualSavings)}/yr
                          </span>
                        )}
                      </div>
                    </div>
                    <span className='text-sm font-semibold text-slate-700 dark:text-stone-300'>
                      {formatPrice(annualPrice)}/yr
                    </span>
                  </label>
                  <label
                    htmlFor='monthly'
                    className={`flex items-center justify-between rounded-lg border p-3 cursor-pointer transition-all ${
                      billingInterval === 'MONTHLY'
                        ? 'border-blue-500 ring-2 ring-blue-500'
                        : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                    }`}>
                    <div className='flex items-center gap-2'>
                      <RadioGroupItem value='MONTHLY' id='monthly' />
                      <span className='font-medium'>Monthly</span>
                    </div>
                    <span className='text-sm font-semibold text-slate-700 dark:text-stone-300'>
                      {formatPrice(monthlyPrice)}/mo
                    </span>
                  </label>
                </RadioGroup>
              </div>
            </div>
          )}

          {/* Step 3: Payment Method */}
          {step === 'payment' && (
            <div className='space-y-4'>
              <div
                className={`rounded-lg border p-4 cursor-pointer transition-all ${
                  collectionMethod === 'AUTOMATIC'
                    ? 'border-blue-500 ring-2 ring-blue-500'
                    : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                }`}
                onClick={() => setCollectionMethod('AUTOMATIC' as CollectionMethod)}>
                <div className='flex items-center gap-3'>
                  <CreditCard className='h-6 w-6 text-slate-600 dark:text-stone-400' />
                  <div>
                    <h3 className='font-medium text-slate-900 dark:text-white'>Credit/Debit Card</h3>
                    <p className='text-sm text-slate-500 dark:text-stone-400'>Automatic monthly payments</p>
                  </div>
                </div>
              </div>

              {/* <div
                className={`rounded-lg border p-4 cursor-pointer transition-all ${
                  collectionMethod === 'SEND_INVOICE'
                    ? 'border-blue-500 ring-2 ring-blue-500'
                    : 'border-slate-200 dark:border-stone-800 hover:border-slate-300'
                }`}
                onClick={() => setCollectionMethod('SEND_INVOICE' as CollectionMethod)}>
                <div className='flex items-center gap-3'>
                  <Building2 className='h-6 w-6 text-slate-600 dark:text-stone-400' />
                  <div>
                    <h3 className='font-medium text-slate-900 dark:text-white'>Wire Transfer / Invoice</h3>
                    <p className='text-sm text-slate-500 dark:text-stone-400'>
                      Pay via bank transfer (Enterprise recommended)
                    </p>
                  </div>
                </div>
              </div> */}
            </div>
          )}

          {/* Step 4: Promo Code & Review */}
          {step === 'promo' && (
            <div className='space-y-6'>
              <div className='p-4 bg-slate-50 dark:bg-stone-900 rounded-lg space-y-2'>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>Plan</span>
                  <span className='font-medium'>{TIER_DISPLAY_NAMES[selectedTier]}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>Storage</span>
                  <span className='font-medium'>
                    {storageBlocks} blocks ({storageBlocks * 100} GB)
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>Billing</span>
                  <span className='font-medium'>{billingInterval === 'MONTHLY' ? 'Monthly' : 'Annual'}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>Payment</span>
                  <span className='font-medium'>{collectionMethod === 'AUTOMATIC' ? 'Card' : 'Invoice'}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-slate-500 dark:text-stone-400'>Estimated Total</span>
                  <span className='font-semibold'>
                    {formatPrice(billingInterval === 'MONTHLY' ? monthlyPrice : annualPrice)}/
                    {billingInterval === 'MONTHLY' ? 'mo' : 'yr'}
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor='promo'>Promo Code (Optional)</Label>
                {promoResult?.valid && promoCode ? (
                  <div className='mt-2 flex items-center justify-between rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20 p-3'>
                    <div className='flex items-center gap-2'>
                      <Tag className='size-4 text-green-600 dark:text-green-400' />
                      <div>
                        <p className='text-sm font-medium text-green-800 dark:text-green-200'>
                          {promoResult.name || promoCode}
                        </p>
                        <p className='text-xs text-green-600 dark:text-green-400'>
                          {promoResult.percentOff
                            ? `${promoResult.percentOff}% off`
                            : promoResult.amountOff
                              ? `${formatPrice(promoResult.amountOff)} off`
                              : 'Discount applied'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='size-7 text-green-600 hover:text-red-500'
                      onClick={() => {
                        setPromoCode('');
                        setPromoResult(null);
                        setPromoInput('');
                      }}>
                      <X className='size-4' />
                    </Button>
                  </div>
                ) : (
                  <div className='mt-2 flex gap-2'>
                    <Input
                      id='promo'
                      placeholder='Enter promo code'
                      value={promoInput}
                      onChange={(e) => {
                        setPromoInput(e.target.value.toUpperCase());
                        if (promoResult && !promoResult.valid) setPromoResult(null);
                      }}
                      disabled={promoValidating}
                      className={promoResult && !promoResult.valid ? 'border-red-300 dark:border-red-700' : ''}
                    />
                    <Button
                      variant='outline'
                      disabled={!promoInput.trim() || promoValidating}
                      onClick={async () => {
                        setPromoValidating(true);
                        try {
                          const data = await execute(validatePromoCodeQuery, { code: promoInput.trim() });
                          const result = data.validatePromoCode;
                          setPromoResult(result);
                          if (result.valid) {
                            setPromoCode(promoInput.trim());
                          }
                        } catch {
                          setPromoResult({ valid: false, name: null, percentOff: null, amountOff: null });
                        } finally {
                          setPromoValidating(false);
                        }
                      }}>
                      {promoValidating ? <Loader2 className='size-4 animate-spin' /> : 'Apply'}
                    </Button>
                  </div>
                )}
                {promoResult && !promoResult.valid && (
                  <p className='text-xs text-red-500 mt-1'>Invalid or expired promo code.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {checkoutError && <p className='text-sm text-red-500 px-1'>{checkoutError}</p>}
        <div className='flex justify-between pt-4 border-t'>
          <Button
            variant='outline'
            onClick={step === 'tier' ? () => onOpenChange(false) : handleBack}
            disabled={loading}>
            {step === 'tier' ? 'Cancel' : 'Back'}
          </Button>

          {step !== 'promo' ? (
            <Button onClick={handleNext}>Continue</Button>
          ) : (
            <Button onClick={handleCheckout} disabled={loading}>
              {loading ? 'Processing...' : 'Proceed to Checkout'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
